import { useState, useEffect } from 'react'
import { FileText } from 'lucide-react'
import { publicAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import { sanitizeHtml } from '../utils/sanitizeHtml'

interface Announcement {
  id: number
  title: string
  content: string
  content_type: string
  is_active: boolean
  created_time: string
  modified_time: string
}

export default function TermsAndConditions() {
  const [terms, setTerms] = useState<Announcement | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchTerms()
  }, [])

  const fetchTerms = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await publicAPI.getAnnouncements({
        content_type: 'generalterms',
      })
      
      // Find the active terms (is_active = true)
      const activeTerms = response.data.data.find(
        (announcement: Announcement) => announcement.is_active === true
      )
      
      if (activeTerms) {
        setTerms(activeTerms)
      } else {
        setError('Ingen aktive vilkår og betingelser funnet.')
      }
    } catch (err: any) {
      console.error('Failed to fetch terms:', err)
      setError('Kunne ikke laste vilkår og betingelser. Vennligst prøv igjen senere.')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="card p-8 text-center">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <ErrorMessage message={error} />
      </div>
    )
  }

  if (!terms) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="card p-8 text-center text-gray-500 dark:text-gray-400">
          Ingen vilkår og betingelser tilgjengelig.
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="card p-8">
        <div className="flex items-center gap-3 mb-6">
          <FileText className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {terms.title}
          </h1>
        </div>

        <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(terms.content) }} />

        {terms.modified_time && (
          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Sist oppdatert: {new Date(terms.modified_time).toLocaleDateString('nb-NO', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
